import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { purchasePremium, restorePurchases as restoreStoreKit, getPremiumProduct } from '../services/storeKitService';
import { App } from '@capacitor/app';

import { keychainService } from '../services/keychainService';
const SubscriptionContext = createContext();

// Premium feature limits
export const LIMITS = {
  free: {
    cards: 1,
    sinkingFunds: 3,
    investmentAccounts: 1,
  },
  premium: {
    cards: 3,
    sinkingFunds: 8,
    investmentAccounts: 5,
  }
};

// Premium-only features
export const PREMIUM_FEATURES = [
  'quiz',              // Should I buy this quiz
  'opportunityCost',   // What Am I Giving Up calculator
  'monthlyDonut',      // Monthly breakdown donut chart
  'yearlyDonut',       // Yearly breakdown donut chart
  'wishlist',          // Wishlist tab
  'netWorthTrends',    // Net worth trend lines and projections
  'netWorthHistory',   // View past months in net worth
  'futureIncome',      // Future income planning
  'recurringTransactions', // Recurring transactions
  'savingsTrends',     // Savings trend chart
  'investmentsTrends', // Investments trend chart
  'yearSummary',       // Year summary stats
  'insights',          // Spending insights on home tab
  'contributionsPlanner', // RRSP/TFSA contributions planner (Plans tab)
  'notesReminders',    // Notes & Reminders page
  'futureWorth',       // Future Worth calculator
];

export const SubscriptionProvider = ({ children, userData, apiUrl, theme }) => {
  const [subscription, setSubscription] = useState(null);
  const [hasPremium, setHasPremium] = useState(() => localStorage.getItem('worthly_premium') === 'true');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(() => localStorage.getItem('worthly_premium') === null);
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [premiumPrice, setPremiumPrice] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null);
  // Tour bypass: while the tour is active (welcome/in_progress/celebration),
  // hasAccess returns true for every premium feature so the user sees a
  // premium-feeling app during onboarding. Reads a window flag set by
  // TourController (it lives in a provider below this one).
  const [tourActive, setTourActive] = useState(
    typeof window !== 'undefined' && !!window.__worthlyTourActive
  );
  useEffect(() => {
    const handler = (e) => setTourActive(!!e?.detail?.active);
    window.addEventListener('worthly:tour-active-change', handler);
    // Initial sync in case the event fired before we mounted.
    if (typeof window !== 'undefined') {
      setTourActive(!!window.__worthlyTourActive);
    }
    return () => window.removeEventListener('worthly:tour-active-change', handler);
  }, []);

  // Fetch subscription status
  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/api/subscription/status`, {
        headers: { 'Authorization': `Bearer ${keychainService.getCached()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription || {});
        setHasPremium(data.has_premium);
        setIsAdmin(data.is_admin);
        localStorage.setItem('worthly_premium', data.has_premium ? 'true' : 'false');
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (userData) {
      fetchSubscriptionStatus();
      getPremiumProduct().then(product => {
        if (product) setPremiumPrice(product.displayPrice);
      });
    }
  }, [userData, fetchSubscriptionStatus]);

  // Re-fetch subscription status when app resumes from background
  useEffect(() => {
    let listener;
    const setupListener = async () => {
      listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && userData) {
          fetchSubscriptionStatus();
        }
      });
    };
    setupListener();
    return () => {
      if (listener) listener.remove();
    };
  }, [userData, fetchSubscriptionStatus]);

  // Check if user has access to a premium feature
  const hasAccess = useCallback((feature) => {
    if (tourActive) return true;      // Tour override — see note above
    if (isAdmin) return true;
    if (hasPremium) return true;
    return !PREMIUM_FEATURES.includes(feature);
  }, [isAdmin, hasPremium, tourActive]);

  // Get limit for a resource
  const getLimit = useCallback((resource) => {
    if (tourActive || isAdmin || hasPremium) {
      return LIMITS.premium[resource] || Infinity;
    }
    return LIMITS.free[resource] || 0;
  }, [isAdmin, hasPremium, tourActive]);

  // Check if user can add more of a resource
  const canAdd = useCallback((resource, currentCount) => {
    const limit = getLimit(resource);
    return currentCount < limit;
  }, [getLimit]);

  // Start free trial
  const startTrial = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/subscription/start-trial`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${keychainService.getCached()}` }
      });
      if (response.ok) {
        await fetchSubscriptionStatus();
        setShowTrialPrompt(false);
        return true;
      }
    } catch (error) {
      console.error('Failed to start trial:', error);
    }
    return false;
  };

  // Skip trial
  const skipTrial = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/subscription/skip-trial`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${keychainService.getCached()}` }
      });
      if (response.ok) {
        await fetchSubscriptionStatus();
        setShowTrialPrompt(false);
        return true;
      }
    } catch (error) {
      console.error('Failed to skip trial:', error);
    }
    return false;
  };

  // Purchase premium via StoreKit
  const purchaseSubscription = async () => {
    setPurchasing(true);
    try {
      const result = await purchasePremium();
      if (result.success) {
        await fetch(`${apiUrl}/api/subscription/activate-premium`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keychainService.getCached()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transaction_id: result.transactionId || result.originalTransactionId,
            original_transaction_id: result.originalTransactionId,
            product_id: result.productId,
            expires_at: result.expiresAt,
          }),
        });
        await fetchSubscriptionStatus();
        dismissPaywall();
        return { success: true };
      } else if (result.cancelled) {
        return { success: false, cancelled: true };
      } else if (result.pending) {
        return { success: false, pending: true };
      }
      return { success: false, error: result.error };
    } catch (error) {
      console.error('Purchase failed:', error);
      return { success: false, error: error.message };
    } finally {
      setPurchasing(false);
    }
  };

  // Restore purchases via StoreKit
  const restoreSubscription = async () => {
    setPurchasing(true);
    try {
      const result = await restoreStoreKit();
      if (result.success && result.found) {
        await fetch(`${apiUrl}/api/subscription/restore`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${keychainService.getCached()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transaction_id: result.transactionId || result.originalTransactionId,
            original_transaction_id: result.originalTransactionId,
            product_id: result.productId,
            expires_at: result.expiresAt,
          }),
        });
        await fetchSubscriptionStatus();
        return { success: true };
      }
      if (result.success === false) {
        return { success: false, error: result.error, debugInfo: result.debugInfo };
      }
      return { success: false, notFound: true, debugInfo: result.debugInfo };
    } catch (error) {
      console.error('Restore failed:', error);
      return { success: false, error: error.message };
    } finally {
      setPurchasing(false);
    }
  };

  // Show paywall for a specific feature
  const showFeaturePaywall = (feature) => {
    setPaywallFeature(feature);
    setShowPaywall(true);
  };

  // Dismiss paywall
  const dismissPaywall = () => {
    setShowPaywall(false);
    setPaywallFeature(null);
  };

  // Effective premium flag: true if the user is actually premium OR the
  // tour is active. Every consumer that reads hasPremium directly
  // (HomeTab, PremiumGate, LimitedGate, etc.) will see premium during the tour.
  const effectiveHasPremium = hasPremium || tourActive;
  // Admin comes ONLY from the backend is_admin flag. The tour unlocks
  // premium via effectiveHasPremium above - it must never grant admin.
  const effectiveIsAdmin = isAdmin;

  const value = {
    subscription,
    hasPremium: effectiveHasPremium,
    isAdmin: effectiveIsAdmin,
    loading,
    hasAccess,
    getLimit,
    canAdd,
    startTrial,
    skipTrial,
    showTrialPrompt,
    setShowTrialPrompt,
    showPaywall,
    paywallFeature,
    showFeaturePaywall,
    dismissPaywall,
    fetchSubscriptionStatus,
    purchaseSubscription,
    restoreSubscription,
    premiumPrice,
    purchasing,
    theme,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
